import {
  ActionIcon,
  Avatar,
  Box,
  Button,
  Group,
  Loader,
  Menu,
  Switch,
  Text,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconBuildingStore,
  IconGripVertical,
  IconPencil,
  IconPhoto,
  IconPlugConnected,
  IconTrash,
} from "@tabler/icons-react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ImageLightbox } from "@/components/ImageLightbox";
import { useAccess } from "@/hooks/useAccess";
import { lockProps } from "@/lib/permissions";
import { formatPrice } from "@/lib/format";

export function SortableProductRow({
  product,
  branches,
  overridesByBranch,
  integrations = [],
  // Eşleştirmeler henüz gelmedi. Boş dizi "eşleşme yok" ile aynı görünüyordu: ikon pasif ve
  // "Eşleştirme yok" başlığıyla çiziliyor, sonra kendiliğinden aktifleşiyordu.
  integrationsLoading = false,
  // Hangi işlemin sürdüğü — bkz. Products.jsx pendingFor(). Yalnızca bu ürüne ait.
  pending = {},
  sortable,
  togglingBranchId,
  onToggleActive,
  onToggleBranch,
  onToggleAllBranches,
  onOpenIntegrations,
  onEdit,
  onEditImage,
  onDelete,
}) {
  // Yetki kontrolleri API'dekiyle aynı ayrımı izler: ürünün kendisi products:write, şube
  // görünürlüğü prices:update (şube override ucu). Entegrasyon durumu ProductIntegrationsModal'da
  // kendi iznine (connections:write) bakıyor.
  //
  // Butonlar gizlenir (ölü kontrol kalmasın), switch'ler yalnızca kilitlenir — durumu görmek
  // yetkiden bağımsız.
  const { can } = useAccess();
  const canWrite = can("products");
  const canBranchOverride = can("products") || can("categories");

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: product.id,
    disabled: !sortable || !canWrite,
  });
  const [zoomed, { open: openZoom, close: closeZoom }] = useDisclosure(false);
  const hasImage = Boolean(product.imageUrl);

  const branchOverride = (branchId) =>
    overridesByBranch[branchId]?.find((item) => item.productId === product.id);

  const hiddenBranchCount = branches.filter(
    (branch) => branchOverride(branch.id)?.isActive === false,
  ).length;
  const anyBranchHidden = hiddenBranchCount > 0;
  const allBranchesHidden = hiddenBranchCount === branches.length;
  // Satır yalnızca ikonun rengini belirlemek için bakıyor; gruplama ve toplu aç/kapat mantığı
  // ProductIntegrationsModal'a taşındı.
  const matchedIntegrations = integrations.filter(
    (item) => item.mapping?.remoteId,
  );
  const anyMappingActive = matchedIntegrations.some(
    (item) => item.mapping.isActive,
  );

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : 1,
    background: isDragging ? "var(--mantine-color-chocolate-0)" : undefined,
  };

  return (
    <Group
      ref={setNodeRef}
      style={style}
      wrap="nowrap"
      gap="md"
      px="sm"
      py="xs"
      bd="1px solid var(--mantine-color-gray-1)"
    >
      <ActionIcon
        variant="subtle"
        color="gray"
        {...attributes}
        {...listeners}
        disabled={!sortable || !canWrite}
        style={{
          cursor: sortable && canWrite ? "grab" : "not-allowed",
          touchAction: "none",
        }}
      >
        <IconGripVertical size={18} />
      </ActionIcon>
      <Avatar
        src={product.imageUrl}
        radius="md"
        size={40}
        component={hasImage ? "button" : undefined}
        type={hasImage ? "button" : undefined}
        aria-label={hasImage ? `${product.name} görselini büyüt` : undefined}
        onClick={hasImage ? openZoom : undefined}
        style={
          hasImage
            ? {
                cursor: "zoom-in",
                border: "none",
                padding: 0,
                background: "transparent",
              }
            : undefined
        }
        title={hasImage ? "Büyüt" : undefined}
      >
        {/* Baş harf yerine ikon: katalogdaki adlar "(G)Cheesecake Lotus" gibi bir kanal
                    önekiyle başlıyor ve ilk karakteri almak her görselsiz satıra "(" basıyordu.
                    Görsel yokluğunu ImagePreview'daki boş durumla aynı ikon anlatıyor. */}
        <IconPhoto size={20} stroke={1.4} />
      </Avatar>
      {hasImage && (
        <ImageLightbox
          opened={zoomed}
          onClose={closeZoom}
          src={product.imageUrl}
          alt={product.name}
        />
      )}
      <Text fw={500} style={{ flex: 1, minWidth: 0 }} truncate>
        {product.name}
      </Text>
      <Text size="sm" w={90} ta="right">
        {formatPrice(product.basePrice)}
      </Text>
      <Text
        size="sm"
        w={90}
        ta="right"
        c={product.integrationPrice == null ? "dimmed" : undefined}
      >
        {product.integrationPrice == null
          ? "—"
          : formatPrice(product.integrationPrice)}
      </Text>
      <Box
        w={70}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 4,
        }}
      >
        <Switch
          aria-label={`${product.name} satışta`}
          checked={product.isActive}
          color="teal"
          disabled={pending.active || !canWrite}
          onChange={(event) =>
            onToggleActive(product, event.currentTarget.checked)
          }
        />
      </Box>
      <Box w={90} style={{ display: "flex", justifyContent: "center" }}>
        {branches.length && product.isActive ? (
          <Menu shadow="md" position="bottom" withinPortal width={320}>
            <Menu.Target>
              <ActionIcon
                variant="light"
                color={anyBranchHidden ? "gray" : "teal"}
                title="Şube görünürlüğü"
              >
                <IconBuildingStore size={18} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>Hangi şubede satışta?</Menu.Label>
              {canBranchOverride && (
                <>
                  <Group gap="xs" px="sm" pb="xs" grow>
                    <Button
                      size="compact-sm"
                      variant="light"
                      color="teal"
                      disabled={!anyBranchHidden}
                      loading={pending.allBranches === true}
                      onClick={() => onToggleAllBranches(product, true)}
                    >
                      Tümünü aç
                    </Button>
                    <Button
                      size="compact-sm"
                      variant="light"
                      color="gray"
                      disabled={allBranchesHidden}
                      loading={pending.allBranches === false}
                      onClick={() => onToggleAllBranches(product, false)}
                    >
                      Tümünü kapat
                    </Button>
                  </Group>
                  <Menu.Divider />
                </>
              )}
              {branches.map((branch) => {
                const override = branchOverride(branch.id);
                const visible = override?.isActive !== false;
                return (
                  <Menu.Item
                    key={branch.id}
                    closeMenuOnClick={false}
                    component="div"
                  >
                    <Group justify="space-between" gap="sm" wrap="nowrap">
                      <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                        <Text size="sm" truncate>
                          {branch.name}
                        </Text>
                      </Group>
                      {togglingBranchId === branch.id ? (
                        <Loader size="sm" color="teal" />
                      ) : (
                        <Switch
                          aria-label={`${branch.name} şubesinde satışta`}
                          checked={visible}
                          color="teal"
                          size="sm"
                          disabled={!canBranchOverride}
                          onChange={(event) => {
                            const { checked } = event.currentTarget;
                            onToggleBranch(product, branch.id, checked);
                          }}
                        />
                      )}
                    </Group>
                  </Menu.Item>
                );
              })}
            </Menu.Dropdown>
          </Menu>
        ) : (
          <ActionIcon
            variant="light"
            color="gray"
            disabled
            title={!branches.length ? "Şube yok" : "Genel durum pasif"}
          >
            <IconBuildingStore size={18} />
          </ActionIcon>
        )}
      </Box>
      <Box w={90} style={{ display: "flex", justifyContent: "center" }}>
        {integrationsLoading ? (
          <ActionIcon
            variant="light"
            color="gray"
            loading
            title="Eşleştirmeler yükleniyor"
          >
            <IconPlugConnected size={18} />
          </ActionIcon>
        ) : matchedIntegrations.length ? (
          <ActionIcon
            variant="light"
            color={anyMappingActive ? "teal" : "gray"}
            title="Entegrasyon durumu"
            onClick={() => onOpenIntegrations(product)}
          >
            <IconPlugConnected size={18} />
          </ActionIcon>
        ) : (
          <ActionIcon
            variant="light"
            color="gray"
            disabled
            title="Eşleştirme yok"
          >
            <IconPlugConnected size={18} />
          </ActionIcon>
        )}
      </Box>
      {/* Yetkisiz kullanıcıda gizlenmiyor, kilitleniyor — satır düzeni herkeste aynı kalsın. */}
      <Group w={108} gap={2} wrap="nowrap" justify="center">
        <ActionIcon
          variant="subtle"
          color="gray"
          {...lockProps(canWrite, { title: "Görsel" })}
          onClick={() => onEditImage(product)}
        >
          <IconPhoto size={18} />
        </ActionIcon>
        <ActionIcon
          variant="subtle"
          color="gray"
          {...lockProps(canWrite, { title: "Düzenle" })}
          onClick={() => onEdit(product)}
        >
          <IconPencil size={18} />
        </ActionIcon>
        <ActionIcon
          variant="subtle"
          color="red"
          {...lockProps(canWrite, { title: "Sil" })}
          onClick={() => onDelete(product)}
        >
          <IconTrash size={18} />
        </ActionIcon>
      </Group>
    </Group>
  );
}
